import * as Sentry from "@sentry/browser";
import { tracker as openReplayTracker } from '@openreplay/tracker'
/**
 * TaskInfoService is a service class responsible for loading and managing task information.
 * It uses HTTP requests to fetch task data and provides methods to access and clear this data.
 */
class TaskInfoService {
    /**
     * Constructs a new instance of TaskInfoService.
     * @param {Object} $http - AngularJS HTTP service for making AJAX requests.
     * @param {Object} LogBridgeService - Service for logging operations.
     * @param {Object} $injector - AngularJS dependency injector service.
     * @param {Object} $q - AngularJS promise service.
     */
    constructor($http, LogBridgeService, $injector, $q) {
        this.$http = $http;
        this.$injector = $injector;
        this.$q = $q;

        this._logIdentifier = 'TaskInfoService';
        this._taskInfo = null;
        this._loadTaskInfoPromise = null;
        this.$logger = LogBridgeService;
    }

    /**
     * Loads task information asynchronously. If the task information is already loaded,
     * it immediately calls the onDataLoaded callback with the cached data. Otherwise,
     * it fetches the data from the server.
     * @param {Function} onDataLoaded - Callback function to be called when data is successfully loaded.
     * @param {Function} onDataLoadFailed - Callback function to be called if data loading fails.
     */
    loadTaskInfo(onDataLoaded, onDataLoadFailed) {
        const _defer = this.$q.defer();
        this._loadTaskInfoPromise = _defer.promise;

        this.$logger.logEnter(this, 'loadTaskInfo');

        if (this._taskInfo != null) {
            onDataLoaded(this._taskInfo);
            _defer.resolve();
            return;
        }
        const $config = this.$injector.get('$config');
        this.$http.get($config.getTaskInfoURL()).then((response) => {
            const TaskInfo = this.$injector.get('TaskInfo');
            this._taskInfo = new TaskInfo(response.data);

            onDataLoaded(this._taskInfo);

            if (this.$injector.has('IFrameAPIService')) {
                const IFrameAPIService = this.$injector.get('IFrameAPIService');
                IFrameAPIService.init(this._taskInfo, true);
            }
            _defer.resolve();            
            
            let title;
            try {
                // Attempt to get the first task title if possible
                const tasks = this._taskInfo.getTasks && this._taskInfo.getTasks();
                if (tasks && Array.isArray(tasks) && tasks.length > 0 && typeof tasks[0].title === 'string' && tasks[0].title.trim() !== '') {
                    title = tasks[0].title;
                } else if (typeof this._taskInfo.getSubjectOrTitle === 'function') {
                    // Fallback to getSubjectOrTitle
                    title = this._taskInfo.getSubjectOrTitle();
                } else {
                    title = '';
                }
            } catch (e) {
                // Failsafe fallback
                if (typeof this._taskInfo.getSubjectOrTitle === 'function') {
                    title = this._taskInfo.getSubjectOrTitle();
                } else {
                    title = '';
                }
            }
            console.log("Setting Sentry tags - title", title, "subject", this._taskInfo.getSubject());
            Sentry.setTag("assessmentTitle", title);
            openReplayTracker.setMetadata("assessmentTitle", title);
            Sentry.setTag("assessmentSubject", this._taskInfo.getSubject());
            openReplayTracker.setMetadata("assessmentSubject", this._taskInfo.getSubject());
        }, (error) => {
            if (isCMS) {
                console.info("Failed with HTTP status code " + error.status + " while loading taskinfo.json, see console for details!");
                console.info("ERROR WHILE LOADING taskinfo.json", error);
            } else {
                console.info("The eAssessment Package is corrupt and cannot be started.");
            }
            if (typeof onDataLoadFailed === 'function') {
                onDataLoadFailed();
            }
            _defer.reject();
        });
    }

    /**
     * Clears the currently stored task information.
     */
    clear() {
        this._taskInfo = null;
    }

    /**
     * Returns the promise associated with the task information loading process.
     * @returns {Promise} A promise that resolves when task information is loaded.
     */
    getLoadTaskInfoPromise() {
        return this._loadTaskInfoPromise;
    }

    /**
     * Retrieves the currently stored task information.
     * @returns {Object} The task information object.
     */
    getTaskInfo() {
        this.$logger.logEnter(this, 'getTaskInfo');
        this.$logger.logExit(this, 'getTaskInfo');
        return this._taskInfo;
    }
}

export default TaskInfoService;